// Página "Eventos Disney" (/eventos): portada con fuegos artificiales, fotos y la pila de pulseras; calendario;
// los 5 eventos como boletos; "¿Cuál es para ti?" y WhatsApp.
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { eventos, paginaEventos, tiposViajero } from '../../data/eventos';
import { ESCRITORIO, eventoPorId, eventosPorId, filasCalendario, fotosVisibles, MESES, precargarEvento, rutaEvento, tituloPestana, TITULO_SITIO, WA_EVENTOS } from '../../services/eventos';
import useEnVista from '../../hooks/useEnVista';
import useMedia from '../../hooks/useMedia';
import useVisto from '../../hooks/useVisto';
import { scrollToId } from '../../utils';
import { alIntentar, Boleto, Enfasis, Estrellas, Fuego, NombreEvento, PilaPulseras, Pieza, Polaroid } from './Piezas';
import './Eventos.css';

/**
 * Lugar de las 3 fotos de la portada: abiertas en abanico, tocándose solo en los bordes (la tercera, solo en escritorio).
 * @type {import('./Piezas').Lugar[]}
 */
const ABANICO = [
  { d: [0, 9.7, 31.7, -6], m: [2.3, 0, 34.3, -6] },
  { d: [27.5, 0, 41.7, 3], m: [40, 4.2, 51.4, 4] },
  { d: [64.2, 12.9, 38.3, -3], m: [0, 0, 0, 0] }
];

function PortadaEventos() {
  const ref = useRef(null);
  const visible = useEnVista(ref);
  const escritorio = useMedia(ESCRITORIO);
  const visibles = fotosVisibles(paginaEventos.fotos, escritorio);
  return (
    <section ref={ref} className={`ev-hub on-dark ev-anim${visible ? '' : ' ev-pausa'}`}>
      <Estrellas n={44} semilla={71} hasta={95} />
      <Fuego x="64.6%" y="19%" color="var(--acc)" retraso={0} largo={48} className="ev-solo-escritorio" />
      <Fuego x="86.8%" y="13.6%" color="#EF5DA8" retraso={-1.2} largo={40} className="ev-solo-escritorio" />
      <Fuego x="92.4%" y="41%" color="#9BE15D" retraso={-2.4} largo={36} className="ev-solo-escritorio" />
      <Fuego x="56.9%" y="43%" color="#6FB3FF" retraso={-0.7} largo={32} className="ev-solo-escritorio" />
      <Fuego x="80%" y="47%" color="var(--acc)" retraso={-0.4} largo={28} className="ev-solo-movil" />
      <Fuego x="18%" y="44%" color="#EF5DA8" retraso={-1.9} largo={22} className="ev-solo-movil" />
      <div className="wrap ev-hub__in">
        <div className="ev-hub__txt">
          <p className="eyebrow">Eventos Disney</p>
          <h1 className="ev-hub__h1"><Enfasis texto={paginaEventos.titulo} /></h1>
          <p className="lead">{paginaEventos.lead}</p>
        </div>
        <div className="ev-hub__visual">
          <div className="ev-abanico">
            {paginaEventos.fotos.map((f, i) => {
              const ev = eventoPorId(f.evento);
              if (!ev || !visibles.includes(f)) return null;
              return (
                <Pieza key={f.src} lugar={ABANICO[i]} flota retraso={-1 - i}>
                  <Link to={rutaEvento(ev)} className="ev-enlace-foto" aria-hidden="true" tabIndex={-1} {...alIntentar(ev)}>
                    <Polaroid foto={f} carga={f === visibles[0] ? 'primero' : 'visible'} />
                  </Link>
                </Pieza>
              );
            })}
          </div>
          <PilaPulseras />
        </div>
        <div className="ev-hub__btns">
          <button type="button" className="ev-btn-oro" onClick={() => scrollToId('eventos')}>Ver los 5 eventos<i className="fa-solid fa-arrow-down" /></button>
          <a className="btn-wa ev-solo-escritorio" href={WA_EVENTOS} target="_blank" rel="noopener noreferrer"><i className="fa-brands fa-whatsapp" />Cotiza tu evento</a>
        </div>
      </div>
    </section>
  );
}

function Calendario() {
  const filas = filasCalendario();
  return (
    <section className="ev-cal sec" aria-label="Calendario de los eventos">
      <div className="wrap">
        <div className="ev-cal__cabeza" data-reveal>
          <div>
            <p className="eyebrow">Calendario</p>
            <h2 className="h2">¿Cuándo se vive <em>cada una</em>?</h2>
          </div>
          <p className="lead">{paginaEventos.calendario}</p>
        </div>
        <div className="ev-cal__tabla" data-reveal>
          <div className="ev-cal__meses" aria-hidden="true">{MESES.map((m) => <span key={m}><b>{m}</b><i>{m[0]}</i></span>)}</div>
          {filas.map(({ evento, izquierda, ancho }) => (
            <div key={evento.id} className="ev-cal__fila">
              <Link to={rutaEvento(evento)} className="ev-cal__nombre" {...alIntentar(evento)}><b><NombreEvento evento={evento} /></b><span>{evento.parque}<i> · {evento.temporadaCorta}</i></span></Link>
              <div className="ev-cal__pista">
                <Link to={rutaEvento(evento)} className="ev-cal__barra" aria-hidden="true" tabIndex={-1} style={{ left: `${izquierda}%`, width: `${ancho}%`, background: evento.tema.banda, color: evento.tema.bandaTexto }} {...alIntentar(evento)}>
                  <span>{evento.temporadaCorta}</span>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Boletos() {
  const ref = useRef(null);
  const visto = useVisto(ref, '300px 0px');
  // quien llega a los boletos está por elegir uno: sus portadas se descargan de antemano
  useEffect(() => {
    if (visto) eventos.forEach(precargarEvento);
  }, [visto]);
  return (
    <section id="eventos" ref={ref} className="ev-boletos sec">
      <div className="wrap">
        <div data-reveal>
          <p className="eyebrow">Los 5 eventos</p>
          <h2 className="h2">Cinco noches, <em>cinco historias</em></h2>
        </div>
        <div className="ev-boletos__lista">
          {eventos.map((e) => <Boleto key={e.id} evento={e} grande />)}
        </div>
        <p className="ev-boletos__desliza" aria-hidden="true">Desliza para ver los 5</p>
      </div>
    </section>
  );
}

function CualEsParaTi() {
  return (
    <section className="ev-cual sec">
      <div className="wrap">
        <div data-reveal>
          <p className="eyebrow">Para cada viajero</p>
          <h2 className="h2">¿Cuál es <em>para ti</em>?</h2>
        </div>
        <div className="ev-cual__lista">
          {tiposViajero.map((tp) => (
            <div key={tp.titulo} className="ev-cual__tarjeta" data-reveal>
              <div className="ev-cual__cabeza">
                <span className="ev-cual__icono"><i className={tp.icono} /></span>
                <h3>{tp.titulo}</h3>
              </div>
              <p>{tp.texto}</p>
              <div className="ev-cual__eventos">
                {eventosPorId(tp.eventos).map((e) => (
                  <Link key={e.id} to={rutaEvento(e)} className="ev-pastilla" style={{ '--banda': e.tema.banda, background: e.tema.oscuro ? e.tema.fondo : e.tema.claro, color: e.tema.oscuro ? e.tema.texto : e.tema.oscuroAcento }} {...alIntentar(e)}><span><NombreEvento evento={e} /></span></Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CtaEventos() {
  const ref = useRef(null);
  const visible = useEnVista(ref);
  return (
    <section ref={ref} className={`ev-cta on-dark ev-anim${visible ? '' : ' ev-pausa'}`}>
      <Estrellas n={30} semilla={73} />
      <div className="ev-cta__deco ev-solo-escritorio" aria-hidden="true">
        <Fuego x="66%" y="26%" color="var(--acc)" retraso={-0.3} largo={44} />
        <Fuego x="84%" y="55%" color="#EF5DA8" retraso={-1.6} largo={36} />
        <Fuego x="74%" y="78%" color="#9BE15D" retraso={-2.8} largo={30} />
      </div>
      <div className="wrap ev-cta__in">
        <div className="ev-cta__txt">
          <p className="eyebrow">¿Te animas?</p>
          <h2 className="h2">¿Cuál vas a <em>vivir tú</em>?</h2>
          <p className="lead">Te ayudo con los boletos, el hotel y el plan de cada noche.</p>
          <div className="ev-cta__accion">
            <a className="btn-wa" href={WA_EVENTOS} target="_blank" rel="noopener noreferrer"><i className="fa-brands fa-whatsapp" />Cotiza tu evento por WhatsApp</a>
            <span>Te respondo personalmente.</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function EventosPagina() {
  useEffect(() => {
    const antes = document.title;
    document.title = tituloPestana();
    return () => { document.title = antes || TITULO_SITIO; };
  }, []);
  return (
    <main className="ev-pagina">
      <PortadaEventos />
      <Calendario />
      <Boletos />
      <CualEsParaTi />
      <CtaEventos />
    </main>
  );
}
